import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../utils';

// Hook per gestire start/stop lavoro per singola installazione (veicolo)
// all'interno di un appuntamento multi-veicolo.
// Nota: usiamo un lock unico per installation.id per evitare doppio click.
export const usePerInstallationWork = (appointment, onUpdate, t) => {
  const [busyInstallationId, setBusyInstallationId] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [expandedEndFormId, setExpandedEndFormId] = useState(null);
  // form-per-installation: { [inst_id]: { status, reason, category } }
  const [endForms, setEndForms] = useState({});

  const LOCK_MS = 2500;
  const releaseLock = (startedAt) => {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, LOCK_MS - elapsed);
    setTimeout(() => setBusyInstallationId(null), remaining);
  };

  const getCurrentLocation = () => new Promise(resolve => {
    if (!navigator.geolocation) return resolve({ latitude: null, longitude: null });
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGettingLocation(false);
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => {
        setGettingLocation(false);
        resolve({ latitude: null, longitude: null });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  const setEndFormField = (instId, field, value) => {
    setEndForms(prev => ({
      ...prev,
      [instId]: { status: 'completed', reason: '', category: '', ...(prev[instId] || {}), [field]: value },
    }));
  };

  const openEndForm = (instId) => {
    setExpandedEndFormId(instId);
    setEndForms(prev => ({
      ...prev,
      [instId]: prev[instId] || { status: 'completed', reason: '', category: '' },
    }));
  };

  const cancelEndForm = () => setExpandedEndFormId(null);

  const startInstallation = async (installationId) => {
    if (busyInstallationId) return;
    const started = Date.now();
    setBusyInstallationId(installationId);
    toast.dismiss();
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const res = await axios.post(
        `${API}/appointments/${appointment.id}/installations/${installationId}/start-work`,
        { latitude, longitude }
      );
      if (res.data?.already_started) {
        toast.info('Lavoro su questo veicolo già avviato');
      } else {
        toast.success('Lavoro avviato sul veicolo');
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || (t && t('errors.generic')) || 'Errore avvio lavoro');
    } finally {
      releaseLock(started);
    }
  };

  const endInstallation = async (installationId) => {
    const form = endForms[installationId] || { status: 'completed', reason: '', category: '' };
    if (form.status === 'not_completed') {
      if (!form.category) {
        toast.error('Seleziona la causale del mancato completamento');
        return;
      }
      if (!form.reason || !form.reason.trim()) {
        toast.error('I dettagli sulla causale sono obbligatori');
        return;
      }
    }
    if (busyInstallationId) return;
    const started = Date.now();
    setBusyInstallationId(installationId);
    toast.dismiss();
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const categoryLabels = {
        cliente_assente: 'Cliente assente',
        veicolo_non_disponibile: 'Veicolo non disponibile',
        materiale_mancante: 'Materiale/Dispositivo mancante',
        dato_errato: 'Dati errati o mancanti',
        impossibilita_tecnica: 'Impossibilità tecnica',
        annullato_cliente: 'Annullato dal cliente',
        condizioni_meteo: 'Condizioni meteo',
        altro: 'Altro',
      };
      const fullReason = form.category
        ? `[${categoryLabels[form.category] || form.category}] ${form.reason || ''}`.trim()
        : (form.reason || null);
      const res = await axios.post(
        `${API}/appointments/${appointment.id}/installations/${installationId}/end-work`,
        {
          latitude, longitude,
          status: form.status,
          reason: fullReason,
          reason_category: form.category || null,
        }
      );
      if (res.data?.already_ended) {
        toast.info('Veicolo già terminato');
      } else {
        toast.success('Lavoro terminato sul veicolo');
      }
      // Se dopo la chiusura l'appuntamento è aggregato in pending_validation / not_completed,
      // il backend lo comunica tramite appointment.status. Il parent farà refresh.
      setExpandedEndFormId(null);
      setEndForms(prev => {
        const { [installationId]: _drop, ...rest } = prev;
        return rest;
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || (t && t('errors.generic')) || 'Errore chiusura lavoro');
    } finally {
      releaseLock(started);
    }
  };

  return {
    busyInstallationId, gettingLocation,
    expandedEndFormId, openEndForm, cancelEndForm,
    endForms, setEndFormField,
    startInstallation, endInstallation,
  };
};
